import { DocNode } from '@atlaskit/adf-schema';
import { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import {
  type Icon,
  type Link,
  type RichText,
  type Status,
  type Tag,
  type User,
  type DatasourceType,
  type DatasourceDataResponseItem,
} from '@atlaskit/linking-types';
import { ConfluencePageAri } from '@atlassian/ari';
import { getCloudId } from '@databases/database-state';
import { ColumnSchema } from '@databases/redux/connections/slice';
import {
  BlogpostReference,
  BlogpostReferenceOrContentId,
  Content,
  PageReference,
  PageReferenceOrContentId,
  ResolvedBlogpostReference,
  ResolvedPageReference,
  SpaceReference,
} from '@databases/types/confluence';
import { format, parseISO } from 'date-fns';

import { encodeHtml } from '../utils';

declare global {
  interface Error {
    _orderlyStatus?: number;
  }
}

/**
 * Reject a promise returned by fetch() if the response status code is an error.
 * Use like this: fetch('url').then(rejectOnError);
 */
export async function rejectOnError<T extends Response>(response: T): Promise<T> {
  if (!response.ok) {
    let body;
    try {
      body = await response.json();
    } catch (err) {
      // Ignore
    }

    const messages = [
      body?.message,
      body?.error,
      ...(body?.data?.errors?.map(
        (e: any) => e?.message?.translation ?? e?.message?.key
      ) ?? []),
    ].filter((m) => !!m);
    const message = messages.length > 0 ? messages.join(' | ') : 'Request failed';
    throw Object.assign(new Error(`${message} (${response.status}, ${response.url})`), {
      _orderlyStatus: response.status,
    });
  }

  return response;
}

/**
 * Put a string in quotes and escape some characters so that it can be used in CQL.
 */
export const quoteCql = (value: string): string => {
  // See https://community.developer.atlassian.com/t/finding-a-page-by-title-certain-cql-queries-crash-the-search/41553 for more details
  return `"${value
    // Quotes should be escaped as \", but since there is no way to match quotes in CQL, let's remove them to avoid crashes
    .replace(/"/g, ' ')
    // Backslashes should be escaped as \\. At the end of the string, they need to be escaped as \\\"
    .replace(/\\/g, (m, o) => (o === value.length - 1 ? '\\\\\\"' : '\\\\'))}"`;
};

/**
 * Put a string in quotes and escape some characters so that it can be used on the right side of a ~ fuzzy match operator in CQL.
 * @param value The search term
 * @param escapeWildcards Whether to leave wildcards (* and ?) as they are or to escape them
 */
export const quoteFuzzyCql = (value: string, escapeWildcards = false): string =>
  // See https://community.developer.atlassian.com/t/finding-a-page-by-title-certain-cql-queries-crash-the-search/41553 for more details
  `"${value
    .replace(
      escapeWildcards
        ? /[!<>=\\"()/[\]^{}#$%'+,.;@_`|~&-*?]/g
        : /[!<>=\\"()/[\]^{}#$%'+,.;@_`|~&-]/g,
      ' '
    )
    .replace(/:/g, '\\:')}"`;

/**
 * Returns a string that contains the space key and content title of the given page (reference), and can thus
 * act as a unique identifier for the page, for example as the key of a map.
 */
export function getPageKey(page: PageReference | Content<'page'>): string {
  const ref = isPageReference(page) ? page : pageToReference(page);
  return `page:${ref.spaceKey.toLowerCase()}:${ref.contentTitle.toLowerCase()}`;
}

/**
 * Returns a string that contains the space key, content title and posting day of the given blogpost (reference), and can thus
 * act as a unique identifier for the blogpost, for example as the key of a map.
 */
export function getBlogpostKey(
  blogpost: BlogpostReference | Content<'blogpost'>
): string {
  const ref = isBlogpostReference(blogpost) ? blogpost : blogpostToReference(blogpost);
  return `blogpost:${ref.spaceKey.toLowerCase()}:${ref.contentTitle.toLowerCase()}:${
    ref.postingDay
  }`;
}

/**
 * Returns a string that can act as the unique identifier for the given page or blogpost, for example as a key in a map.
 */
export function getContentKey(
  content: PageReference | Content<'page'> | BlogpostReference | Content<'blogpost'>
): string {
  return content.type === 'blogpost' ? getBlogpostKey(content) : getPageKey(content);
}

/**
 * Returns a resolved page reference that represents the given page.
 */
export function pageToReference(page: Content<'page'>): ResolvedPageReference {
  return {
    type: 'page',
    spaceKey: page.space!.key,
    contentTitle: page.title,
    contentId: page.id,
    ari: ConfluencePageAri.create({
      siteId: getCloudId()!,
      pageId: page.id,
    }).toString(),
    url: `${page._links.self!.replace(/\/rest\/api\/content\/[0-9]*$/, '')}${page._links
      .webui!}`,
  };
}

/**
 * Returns a resolved blogpost reference that represents the given blogpost.
 * Note that for blogpost drafts that are not created yet, the postingDay will be empty, so the returned object is not
 * suitable to uniquely identify the blogpost.
 */
export function blogpostToReference(
  blogpost: Content<'blogpost'>
): ResolvedBlogpostReference {
  return {
    type: 'blogpost',
    spaceKey: blogpost.space!.key,
    contentTitle: blogpost.title,
    postingDay: blogpost.history!.createdDate
      ? format(parseISO(blogpost.history!.createdDate), 'yyyy/MM/dd')
      : '',
    contentId: blogpost.id,
    url: `${blogpost._links.self!.replace(/\/rest\/api\/content\/[0-9]*$/, '')}${blogpost
      ._links.webui!}`,
  };
}

export function contentToReference(
  content: Content<'page'> | Content<'blogpost'>
): ResolvedPageReference | ResolvedBlogpostReference {
  switch (content.type) {
    case 'page':
      return pageToReference(content);

    case 'blogpost':
      return blogpostToReference(content);
  }
}

export function isPageReference(
  value: Content | PageReferenceOrContentId | BlogpostReferenceOrContentId
): value is PageReference {
  return 'contentTitle' in value && value.type === 'page';
}

export function isBlogpostReference(
  value: Content | PageReferenceOrContentId | BlogpostReferenceOrContentId
): value is BlogpostReference {
  return 'contentTitle' in value && value.type === 'blogpost';
}

export function isContent(
  value: Content | PageReferenceOrContentId | BlogpostReferenceOrContentId
): value is Content {
  return 'title' in value;
}

/**
 * Encodes a page reference to XHTML storage format.
 * @param link If true, the ri:page reference will be wrapped in an ac:link element.
 */
export function serializePageReference(ref: PageReference, link = false): string {
  const page = `<ri:page
    ri:space-key="${encodeHtml(ref.spaceKey)}"
    ri:ari="${encodeHtml(ref.ari)}"
    ri:content-title="${encodeHtml(ref.contentTitle)}"
  />`;
  return link ? `<ac:link>${page}</ac:link>` : page;
}

/**
 * Encodes a content ID reference to XHTML storage format.
 * @param link If true, the ri:page reference will be wrapped in an ac:link element.
 */
export function serializeContentIdReference(
  ref: { contentId: string },
  link = false
): string {
  const page = `<ri:content-entity ri:content-id="${encodeHtml(ref.contentId)}" />`;
  return link ? `<ac:link>${page}</ac:link>` : page;
}

/**
 * Encodes a page reference to XHTML storage format.
 * @param link If true, the ri:page reference will be wrapped in an ac:link element.
 */
export function serializeBlogpostReference(ref: BlogpostReference, link = false): string {
  const blogpost = `<ri:blog-post
    ri:space-key="${encodeHtml(ref.spaceKey)}"
    ri:content-title="${encodeHtml(ref.contentTitle)}"
    ri:posting-day="${encodeHtml(ref.postingDay)}"
    ri:ari="${encodeHtml(ref.ari)}"
  />`;
  return link ? `<ac:link>${blogpost}</ac:link>` : blogpost;
}

/**
 * Encodes a space reference to XHTML storage format.
 */
export function serializeSpaceReference(ref: SpaceReference): string {
  return `<ri:space ri:space-key="${encodeHtml(ref.key)}"/>`;
}

/**
 * Encodes a user reference to XHTML storage format.
 * @param link If true, the ri:user reference will be wrapped in an ac:link element. User mentions in Confluence are always
 *             wrapped by a link.
 */
export function serializeUserReference(accountId: string, link = false): string {
  const user = `<ri:user ri:account-id="${encodeHtml(accountId)}"/>`;
  return link ? `<ac:link>${user}</ac:link>` : user;
}

/**
 * Returns the path (without the base URL) that a content label should link to.
 */
export function getLabelPath(spaceKey: string, label: string): string {
  return `/label/${encodeURIComponent(spaceKey)}/${encodeURIComponent(label)}`;
}

function adfToText(value: RichText): string {
  try {
    if (value.type === 'adf') {
      const adf = JSON.parse(value.text) as DocNode;
      return PMNode.fromJSON(defaultSchema, {
        ...adf,
        content: [...adf.content.slice(0, 2)],
      }).textContent;
    }
    return '';
  } catch (e) {
    return '';
  }
}

function getTextFromSingleDatasourceValue(
  type: ColumnSchema['type'],
  value: DatasourceType['value']
): string {
  switch (type) {
    case 'icon':
      return (value as Icon)?.label ?? '';
    case 'status':
      return (value as Status)?.text;
    case 'link':
      return (value as Link)?.text ?? (value as Link)?.url ?? '';
    case 'tag':
      return (value as Tag)?.text;
    case 'user':
      return (value as User)?.displayName ?? '';
    case 'richtext':
      if ((value as RichText)?.type === 'adf') {
        return adfToText(value as RichText);
      }
      return (value as RichText)?.text;
    default:
      return value.toString();
  }
}

export function getTextFromDatasourceValue(
  column: ColumnSchema,
  item: DatasourceDataResponseItem
): string {
  try {
    const value = item[column.key];
    if (!value || !value.data) {
      return '';
    }

    if (Array.isArray(value.data)) {
      return value.data
        .map((data) => getTextFromSingleDatasourceValue(column.type, data))
        .join(', ');
    } else {
      return getTextFromSingleDatasourceValue(column.type, value.data);
    }
  } catch {
    return '';
  }
}
